import { apiFetch, parseApiResponse } from '../../../utils/apiClient';
import {
  buildPartnerPayload,
  normalizePartner,
  normalizePartnerAgency,
  normalizePartnerContact,
} from './partnerModel';
import {
  buildPartnerQuoteSettingsPayload,
  normalizePartnerQuoteSettings,
} from './partnerQuoteSettingsModel';

const request = async (url, options) => parseApiResponse(await apiFetch(url, options));

const partnerQueryFields = [
  'search',
  'socialReason',
  'siret',
  'postalCode',
  'city',
  'role',
  'lifecycleStatus',
  'relationshipStatus',
  'domainId',
  'hasOpenAffairs',
  'hasActiveProjects',
];

const buildPartnerQuery = (filters = {}) => {
  const params = new URLSearchParams();
  partnerQueryFields.forEach((field) => {
    const value = filters[field];
    if (value !== undefined && value !== null && value !== '') params.set(field, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

const contactPayload = (contact = {}) => {
  const normalized = normalizePartnerContact(contact);
  return {
    id: normalized.id || undefined,
    firstName: normalized.firstName,
    lastName: normalized.lastName,
    contactName: normalized.contactName,
    agencyId: normalized.agencyId || undefined,
    agencyClientKey: normalized.agencyClientKey || undefined,
    role: normalized.role,
    email: normalized.email,
    phone: normalized.phone,
    mobilePhone: normalized.mobilePhone,
    notes: normalized.notes,
  };
};

const agencyPayload = (agency = {}) => {
  const normalized = normalizePartnerAgency(agency);
  return {
    id: normalized.id || undefined,
    clientKey: normalized.clientKey || undefined,
    name: normalized.name,
    socialReason: normalized.socialReason,
    addressLine1: normalized.addressLine1,
    addressLine2: normalized.addressLine2,
    address: normalized.address,
    city: normalized.city,
    regionOrState: normalized.regionOrState,
    postalCode: normalized.postalCode,
    countryCode: normalized.countryCode,
    phone: normalized.phone,
    email: normalized.email,
    website: normalized.website,
    notes: normalized.notes,
  };
};

const createPayload = (payload = {}) => ({
  ...buildPartnerPayload(payload),
  contacts: (Array.isArray(payload.contacts) ? payload.contacts : []).map(contactPayload),
  agencies: (Array.isArray(payload.agencies) ? payload.agencies : []).map(agencyPayload),
});

export const getPartners = async (filters = {}) => {
  const partners = await request(`/api/partners${buildPartnerQuery(filters)}`);
  return (Array.isArray(partners) ? partners : []).map(normalizePartner);
};

export const getPartner = async (id) => normalizePartner(await request(`/api/partners/${id}`));

export const createPartner = async (payload) => normalizePartner(await request('/api/partners', {
  method: 'POST',
  body: JSON.stringify(createPayload(payload)),
}));

export const updatePartner = async (id, payload) => normalizePartner(await request(`/api/partners/${id}`, {
  method: 'PUT',
  body: JSON.stringify(buildPartnerPayload(payload)),
}));

export const verifyPartner = async (id) => normalizePartner(await request(`/api/partners/${id}/verify`, {
  method: 'PUT',
}));

export const getPartnerAgencies = async (id) => {
  const agencies = await request(`/api/partners/${id}/agencies`);
  return (Array.isArray(agencies) ? agencies : []).map(normalizePartnerAgency);
};

export const updatePartnerAgencies = async (id, agencies) => normalizePartner(
  await request(`/api/partners/${id}/agencies`, {
    method: 'PUT',
    body: JSON.stringify({
      agencies: (Array.isArray(agencies) ? agencies : []).map(agencyPayload),
    }),
  }),
);

export const updatePartnerContacts = async (id, contacts) => normalizePartner(
  await request(`/api/partners/${id}/contacts`, {
    method: 'PUT',
    body: JSON.stringify({
      contacts: (Array.isArray(contacts) ? contacts : []).map(contactPayload),
    }),
  }),
);

export const getPartnerCommercialOverview = async (id) => {
  const overview = await request(`/api/partners/${id}/commercial-overview`);
  return {
    affairs: Array.isArray(overview?.affairs) ? overview.affairs : [],
    projects: Array.isArray(overview?.projects) ? overview.projects : [],
    quotes: Array.isArray(overview?.quotes) ? overview.quotes : [],
    activities: Array.isArray(overview?.activities) ? overview.activities : [],
    documents: Array.isArray(overview?.documents) ? overview.documents : [],
    metrics: overview?.metrics && typeof overview.metrics === 'object' ? overview.metrics : {},
  };
};

export const createPartnerActivity = async (id, payload) => request(`/api/partners/${id}/activities`, {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const getPartnerMarketingScore = async (id) => request(`/api/prospecting/${id}/score`);

export const getCompanyDomains = async (parentId) => {
  const params = new URLSearchParams();
  if (parentId !== undefined && parentId !== null && parentId !== '') params.set('parentId', parentId);
  const query = params.toString();
  return request(`/api/partners/domains${query ? `?${query}` : ''}`);
};


export const getPartnerQuoteSettings = async (id) => normalizePartnerQuoteSettings(
  await request(`/api/partners/${id}/quote-settings`),
);

export const updatePartnerQuoteSettings = async (id, settings) => normalizePartnerQuoteSettings(
  await request(`/api/partners/${id}/quote-settings`, {
    method: 'PUT',
    body: JSON.stringify(buildPartnerQuoteSettingsPayload(settings)),
  }),
);
